import type React from 'react';
import type { RefObject } from 'react';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import type { InitialConfigType } from '@lexical/react/LexicalComposer';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import type { ContentEditableProps } from '@lexical/react/LexicalContentEditable';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import type { LexicalEditor as Editor } from 'lexical';
import { ParagraphNode, TextNode } from 'lexical';

import { cn } from '@polyai/ui/utils/cn';

import { ActionNode } from 'components/molecules/LexicalEditor/features/action';
import { ActionHandlePlugin } from 'components/molecules/LexicalEditor/plugins/ActionHandlePlugin';
import { useToast } from 'hooks/useToast';
import EditorStatePlugin from './plugins/EditorState';
import type { Nodes } from './types';

export type { ContentEditableProps };

export type LexicalEditorProps = ContentEditableProps & {
  dataId?: string;
  children?: React.ReactNode;
  nodes?: Nodes;
  namespace?: string;
  showHandle?: boolean;
  staticHandlePosition?: boolean;
  autoFocus?: boolean;
  inputRef?: RefObject<HTMLDivElement | null>;
};

const defaultNodes: InitialConfigType['nodes'] = [
  ParagraphNode,
  TextNode,
  ActionNode,
];

export const LexicalEditor: React.FC<LexicalEditorProps> = forwardRef<
  { getEditor: () => Editor | undefined },
  LexicalEditorProps
>(
  (
    {
      children,
      nodes,
      namespace,
      readOnly,
      showHandle,
      disabled,
      dataId,
      staticHandlePosition,
      autoFocus,
      inputRef,
      height,
      placeholder,
      className,
      ...rest
    },
    ref,
  ) => {
    const toast = useToast();
    const editorRef = useRef<Editor>(undefined);

    const [floatingAnchorElem, setFloatingAnchorElem] =
      useState<HTMLDivElement | null>(null);

    const onRef = (_floatingAnchorElem: HTMLDivElement) => {
      if (_floatingAnchorElem !== null) {
        setFloatingAnchorElem(_floatingAnchorElem);
      }
    };

    const editable = !disabled && !readOnly;

    const initialConfig: InitialConfigType = {
      namespace: namespace ? namespace : `RTI_${dataId}`,
      onError: err => {
        toast.error({
          title: 'An error occurred with the rich text editor. ',
          description: err.message,
        });
      },
      nodes: [...defaultNodes, ...(nodes ? nodes : [])],
      editable,
    };

    useImperativeHandle(ref, () => {
      return {
        getEditor: () => editorRef.current,
      };
    });

    const editableId = useMemo(() => editorRef.current?.getKey(), []);

    return (
      <div ref={inputRef} className="relative" data-rti-id={dataId}>
        <LexicalComposer initialConfig={initialConfig}>
          <EditorStatePlugin editable={editable} />
          <EditorRefPlugin editorRef={editorRef} />
          <RichTextPlugin
            ErrorBoundary={LexicalErrorBoundary}
            contentEditable={
              <div
                ref={onRef}
                className={cn(
                  'relative',
                  "[&>div:has(span[id='lexical-placeholder'])]:pointer-events-none",
                  "[&>div:has(span[id='lexical-placeholder'])]:absolute",
                  "[&>div:has(span[id='lexical-placeholder'])]:z-[1]",
                  "[&>div:has(span[id='lexical-placeholder'])]:top-0",
                  "[&>div:has(span[id='lexical-placeholder'])]:left-0",
                  "[&>div:has(span[id='lexical-placeholder'])]:px-sm1",
                  "[&>div:has(span[id='lexical-placeholder'])]:py-xs3",
                  "[&>div:has(span[id='lexical-placeholder'])]:font-[inherit]",
                  "[&>div:has(span[id='lexical-placeholder'])]:text-body-regular",
                  "[&>div:has(span[id='lexical-placeholder'])]:font-regular",
                  "[&>div:has(span[id='lexical-placeholder'])]:leading-normal",
                  "[&>div:has(span[id='lexical-placeholder'])]:text-placeholder",
                )}
                style={{ minHeight: height }}
              >
                <ContentEditable
                  {...(rest as any)}
                  disabled={disabled}
                  id={editableId}
                  className={cn(
                    'inline-block box-border border border-border-strong rounded-small',
                    'w-full h-full min-h-[2.8125rem]',
                    'pt-xs3 pb-xs3 pl-sm1 pr-md1',
                    'text-body-regular font-regular leading-normal whitespace-pre-wrap',
                    'focus:outline-none focus:border-border-primary',
                    'focus-within:border-border-primary',
                    'placeholder:text-placeholder',
                    disabled && 'text-disabled bg-background-page',
                    className,
                  )}
                  placeholder={(isEditable: boolean) => (
                    <span id="lexical-placeholder">
                      {typeof placeholder === 'function'
                        ? placeholder(isEditable)
                        : placeholder}
                    </span>
                  )}
                />
              </div>
            }
          />
          {autoFocus && <AutoFocusPlugin defaultSelection="rootEnd" />}
          <HistoryPlugin />
          {floatingAnchorElem && showHandle && (
            <ActionHandlePlugin
              anchorElem={floatingAnchorElem}
              staticHandlePosition={staticHandlePosition}
            />
          )}
          {children}
        </LexicalComposer>
      </div>
    );
  },
);
